import React, { useState } from 'react';
import {
  CardContents,
  TitleContainer,
  CardTitle,
  CancelButton,
  FormError,
  PrimaryButton,
  ButtonContainer,
  InputContainer,
  Selector,
  AmountInput,
  SecondaryButton,
  Padding,
} from 'common/components/cardStyles';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { AccountTransferChoices, defaultAPIRequestState } from 'api';
import { AccountTransfer } from 'common/state/accounts/transfers/actions';
import {
  acceptAccountTransferRecipe,
  declineAccountTransferRecipe,
  newAccountTransferRecipe,
} from 'common/state/accounts/transfers/recipes';
import {
  doAcceptAccountTransfer,
  doDeclineAccountTransfer,
  doNewAccountTransfer,
} from 'common/state/accounts/transfers/thunkActions';

interface TransferModal {
  selectedRow: AccountTransfer;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

interface TransferNewModal {
  choices: AccountTransferChoices;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

export function TransferNewModal({
  choices,
  setShouldRefresh,
  closeModal,
}: TransferNewModal) {
  const { account, currency, status, wallet } = choices;

  const [amount, setAmount] = useState('');

  // The currently selected account
  const [selectedAccount, setSelectedAccount] = useState<null | ElementType<
    typeof account
  >>(null);
  // The currently selected status
  const [selectedStatus, setSelectedStatus] = useState<null | ElementType<
    typeof status
  >>(null);
  // The currently selected currency
  const [selectedCurrency, setSelectedCurrency] = useState<null | ElementType<
    typeof currency
  >>(null);
  // The currently selected wallet
  const [selectedWallet, setSelectedWallet] = useState<null | ElementType<
    typeof wallet
  >>(null);

  // Function that clears all fields
  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setAmount('');
    setSelectedAccount(null);
    setSelectedWallet(null);
    setSelectedStatus(null);
    setSelectedCurrency(null);
  }, [setAmount]);

  // The state of the new transfer request
  const [state, dispatch] = useProducerWithThunks(
    newAccountTransferRecipe,
    defaultAPIRequestState
  );
  // New transfer request callback for form
  const submitNewTransfer: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doNewAccountTransfer({
          account: selectedAccount.value,
          amount,
          currency: selectedCurrency.value,
          status: selectedStatus.value,
          wallet: selectedWallet.value,
        })
      );
    },
    [
      dispatch,
      amount,
      selectedAccount,
      selectedCurrency,
      selectedStatus,
      selectedWallet,
    ]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitNewTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Accept Amount</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <InputContainer>
          <Padding />
          <Selector
            label="Status *"
            shrinked
            options={status}
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Account *"
            shrinked
            options={account}
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e)}
          />
          <Selector
            label="Wallet *"
            shrinked
            options={wallet}
            value={selectedWallet}
            onChange={(e) => setSelectedWallet(e)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Currency *"
            shrinked
            options={currency}
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e)}
          />
          <AmountInput
            currency={selectedCurrency?.value || 'NONE'}
            shrinked
            label="Amount *"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton
            type="submit"
            disabled={
              !amount ||
              !selectedAccount ||
              !selectedCurrency ||
              !selectedStatus ||
              !selectedWallet
            }
          >
            Create
          </PrimaryButton>
          <SecondaryButton type="button" onClick={clearTransaction}>
            Clear
          </SecondaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export function TransferAcceptModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: TransferModal) {
  const { url } = selectedRow;

  // The state of the deposit request
  const [state, dispatch] = useProducerWithThunks(
    acceptAccountTransferRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitAcceptAccountTransfer: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(doAcceptAccountTransfer(url));
    },
    [dispatch, url]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitAcceptAccountTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Accept Account Transfer</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export function TransferDeclineModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: TransferModal) {
  const { url } = selectedRow;

  // The state of the deposit request
  const [state, dispatch] = useProducerWithThunks(
    declineAccountTransferRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitDeclineAccountTransfer: React.FormEventHandler =
    React.useCallback(
      (e) => {
        e.preventDefault();
        dispatch(doDeclineAccountTransfer(url));
      },
      [dispatch, url]
    );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitDeclineAccountTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Decline Account Transfer</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}
